import React, {useState, useRef} from "react"
import classNames from "classnames"
import {EditMode} from "../../../../resources/common"
import {CustomerInterestTier} from "../../../../resources/depositProduct"

interface TierRowProps {
	index: number
	tier: CustomerInterestTier
	isFirst: boolean
	isSecond: boolean
	editMode: EditMode
	isDisabled?: boolean
	onChange: (tier: CustomerInterestTier) => void
	onDelete: () => void
}

// Formula builder chips for formula mode
const FORMULA_CHIPS = ["FED_UPPER_BOUND", "FED_LOWER_BOUND", "+", "-", "/", "*", "(", ")"]

// Format cents to whole dollars display (no fractions)
function centsToDollars(cents: number): string {
	const dollars = Math.floor(cents / 100)
	return dollars.toLocaleString("en-US", {minimumFractionDigits: 0, maximumFractionDigits: 0})
}

// Parse whole dollars string to cents (no fractions allowed)
function dollarsToCents(dollars: string): number {
	// Remove commas and any decimal portion
	const cleaned = dollars.replace(/,/g, "").split(".")[0]
	const parsed = parseInt(cleaned, 10)
	return isNaN(parsed) ? 0 : parsed * 100
}

// Check if formula is a simple basis points number (or empty, which defaults to basis points mode)
function isBasisPoints(formula: string): boolean {
	const trimmed = formula.trim()
	return trimmed === "" || /^\d+$/.test(trimmed)
}

export default function TierRow({
	index,
	tier,
	isFirst,
	isSecond,
	editMode,
	isDisabled = false,
	onChange,
	onDelete,
}: TierRowProps) {
	const isViewMode = editMode === EditMode.VIEW
	const controlsDisabled = isViewMode || isDisabled
	const [showFormulaInput, setShowFormulaInput] = useState(!isBasisPoints(tier.formula))
	const formulaInputRef = useRef<HTMLInputElement>(null)

	const handleLowerBoundChange = (value: string) => {
		onChange({...tier, lowerBound: dollarsToCents(value)})
	}

	const handleHigherBoundChange = (value: string) => {
		if (value === "") {
			onChange({...tier, higherBound: undefined})
		} else {
			onChange({...tier, higherBound: dollarsToCents(value)})
		}
	}

	const handleFormulaChange = (value: string) => {
		onChange({...tier, formula: value})
	}

	const handleChipClick = (chip: string) => {
		onChange({...tier, formula: tier.formula + chip})
		formulaInputRef.current?.focus()
	}

	const handleBoundTypeChange = (type: "upTo" | "andAbove") => {
		if (type === "andAbove") {
			onChange({...tier, higherBound: undefined})
		} else {
			// Set a default upper bound if switching to "Up to"
			onChange({...tier, higherBound: tier.lowerBound ? tier.lowerBound + 100000 : undefined}) // Default $1000 range
		}
	}

	const boundType = tier.higherBound === undefined ? "andAbove" : "upTo"

	return (
		<div className={classNames("tier-row", "edit-mode", controlsDisabled && "controls-disabled")}>
			<div className="tier-header">
				<span className="tier-label">Tier {index + 1}</span>
				{!isFirst && !isSecond && !controlsDisabled && (
					<button type="button" className="delete-tier-button" onClick={onDelete} title="Delete tier">
						×
					</button>
				)}
			</div>
			{/* Row 1: From / Up to / Amount fields */}
			<div className="tier-bounds-row">
				<span className="field-label">From</span>
				<div className="input-with-prefix">
					<span className="input-prefix">$</span>
					<input
						type="text"
						className="input"
						value={tier.lowerBound !== undefined ? centsToDollars(tier.lowerBound) : ""}
						onChange={(e) => handleLowerBoundChange(e.target.value)}
						disabled={isFirst || controlsDisabled}
					/>
				</div>
				<select
					className="select bound-type-select"
					value={boundType}
					onChange={(e) => handleBoundTypeChange(e.target.value as "upTo" | "andAbove")}
					disabled={isFirst || controlsDisabled}
				>
					<option value="upTo">Up to</option>
					<option value="andAbove">And above</option>
				</select>
				<div className="input-with-prefix">
					<span className="input-prefix">$</span>
					<input
						type="text"
						className="input"
						value={tier.higherBound !== undefined ? centsToDollars(tier.higherBound) : ""}
						onChange={(e) => handleHigherBoundChange(e.target.value)}
						placeholder=""
						disabled={controlsDisabled || (!isFirst && boundType === "andAbove")}
					/>
				</div>
			</div>
			{/* Row 2: Rate/Formula input - use spacer to align with $ inputs above */}
			<div className="tier-formula-row">
				<span className="field-spacer"></span>
				<div className="formula-input-wrapper">
					<input
						ref={formulaInputRef}
						type="text"
						className={classNames("input", "formula-input", showFormulaInput && "formula-mode")}
						value={tier.formula}
						onChange={(e) => handleFormulaChange(e.target.value)}
						placeholder={
							showFormulaInput ? "Enter formula or select variables from below" : "Enter basis points (300 = 3%)"
						}
						disabled={controlsDisabled}
					/>
					{!controlsDisabled && (
						<button
							type="button"
							className="switch-input-button"
							onClick={() => setShowFormulaInput(!showFormulaInput)}
						>
							↻ {showFormulaInput ? "Switch to basis points" : "Switch to formula"}
						</button>
					)}
				</div>
			</div>
			{/* Row 3: Formula chips - shown when in formula mode and not disabled */}
			{showFormulaInput && !controlsDisabled && (
				<div className="tier-formula-row">
					<span className="field-spacer"></span>
					<div className="formula-chips">
						{FORMULA_CHIPS.map((chip) => (
							<button key={chip} type="button" className="formula-chip" onClick={() => handleChipClick(chip)}>
								{chip}
							</button>
						))}
					</div>
				</div>
			)}
		</div>
	)
}
